// Types based on mercury-hub/src/structs/ai_tools.rs

export interface UserPlanInfo {
  userId: string;
  planType: string;
  monthlyExecutionLimit: number;
  executionsUsedThisMonth: number;
  nextResetDate: string;
  availableTools: string[];
  hasFileUpload: boolean;
  hasAuctionLinking: boolean;
}

export interface VisualSettings {
  userId: string;
  brandColor: string;
  accentColor: string;
  textColor: string;
  headerColor: string;
  documentBackground: string;
  headerFont: string;
  bodyFont: string;
  baseFontSize: string;
  contentDensity: string;
  pageMargins: string;
  cornerRadius: string;
  borderWeight: string;
  showShadows: boolean;
  showLogo: boolean;
  logoUrl: string;
  companyName: string;
  headerSeparatorStyle: string;
  lastModified: string;
}

export interface AIActionButton {
  id: string;
  label: string;
  actionType: string;
  description: string;
  requiresUserInput: boolean;
  userInputLabel?: string;
  userInputPlaceholder?: string;
  userInputDescription?: string;
  isDynamic: boolean;
  order?: number;
}

export interface SelectOption {
  value: string;
  label: string;
  icon?: string;
  description?: string;
}

export interface FormFieldDefinition {
  id: string;
  label: string;
  fieldType: string;
  placeholder?: string;
  description?: string;
  required: boolean;
  minLength?: number;
  maxLength?: number;
  selectOptions?: SelectOption[];
  validation?: Record<string, unknown>;
  defaultValue?: unknown;
  order?: number;
  visibleInResult?: boolean;
  dependsOn?: string;
  dependsOnValue?: unknown;
  editable: boolean;

  // Numeric/Range fields
  min?: number;
  max?: number;
  step?: number;

  // Text multiline
  minLines?: number;
  maxLines?: number;

  // Input formatting
  maskPattern?: string;
  keyboardType?: string;

  // File upload
  allowedExtensions?: string[];
  allowMultiple?: boolean;
  maxFileSize?: number;

  // Select enhancements
  enableFilter?: boolean;
  allowClear?: boolean;

  // Display
  icon?: string;
  helper?: string;
}

export interface FormDefinition {
  fields: FormFieldDefinition[];
  description?: string;
  allowAttachments: boolean;
  allowAuctionLinking: boolean;
  allowCustomProfiles: boolean;
}

export interface CanvasSettings {
  brandColor?: string;
  accentColor?: string;
  fontFamily?: string;
  baseFontSize?: number;
  contentPadding?: number;
  borderRadius?: number;
  showLogo?: boolean;
  showHeader?: boolean;
  headerStyle?: string;
}

export interface CanvasTemplate {
  type: string;
  baseTemplate?: string;
  jsonSchema?: string;
  htmlFileBase64?: string;
  htmlFileName?: string;
  cssFileBase64?: string;
  cssFileName?: string;
  editableFields: string[];
  canvasSettings: CanvasSettings;
  isDynamic: boolean;
}

export interface AITool {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  categoryColor: string;
  tags: string[];
  minPlanRequired: number;
  executionsPerMonth: number;
  formTemplate: FormDefinition;
  canvasTemplate: CanvasTemplate;
  actionButtons: AIActionButton[];
  lastUsedDate: string;
  usageCount: number;
  aiPrompt: string;
  aiSystemInstruction: string;
}

export interface AITaskInstanceHistory {
  version: number;
  title: string;
  formDataSnapshot: Record<string, unknown>;
  resultSnapshot?: Record<string, unknown>;
  createdAt: string;
  changeDescription?: string;
}

export interface AITaskInstance {
  id: string;
  toolId: string;
  userId: string;
  name: string;
  comment?: string;
  tags: string[];
  formData: Record<string, unknown>;
  linkedAuctionId?: string;
  createdAt: string;
  modifiedAt?: string;
  lastGeneratedAt?: string;
  status: string;
  history: AITaskInstanceHistory[];
  attachmentIds: string[];
  lastResult?: Record<string, unknown>;
  visualSettings: VisualSettings;
}

export type ExtractionStatus = 'Pending' | 'Processing' | 'Completed' | 'Failed';

export interface AiFileMetadata {
  id: string;
  taskInstanceId: string;
  userId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  storageBucket: string;
  storageKey: string;
  uploadedAt: string;
  extractedText?: string;
  extractionStatus: ExtractionStatus;
  extractionError?: string;
  extractedPageCount?: number;
  extractedAt?: string;
}

// Form field types enum
export const FIELD_TYPES = [
  { value: 'text', label: 'Tekst (jednolinijkowy)' },
  { value: 'textarea', label: 'Tekst wielolinijkowy' },
  { value: 'number', label: 'Liczba' },
  { value: 'select', label: 'Lista rozwijana' },
  { value: 'multiselect', label: 'Wielokrotny wybór' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio buttons' },
  { value: 'date', label: 'Data' },
  { value: 'datetime', label: 'Data i czas' },
  { value: 'file', label: 'Plik' },
  { value: 'color', label: 'Kolor' },
  { value: 'slider', label: 'Suwak' },
  { value: 'segmented', label: 'Segmented control' },
] as const;

// Action types enum
export const ACTION_TYPES = [
  { value: 'export', label: 'Eksport' },
  { value: 'regenerate', label: 'Regeneruj' },
  { value: 'modify', label: 'Modyfikuj' },
  { value: 'copy', label: 'Kopiuj' },
  { value: 'share', label: 'Udostępnij' },
  { value: 'download', label: 'Pobierz' },
  { value: 'custom', label: 'Niestandardowa' },
] as const;
